import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSeedanceAuthStore } from '../../store/seedanceAuthStore';
import { useSeedanceTaskStore } from '../../store/seedanceTaskStore';
import { useSeedanceUiStore } from '../../store/seedanceUiStore';
import {
  buildRegenerateDraftFromTaskDetail,
  type VideoRegenerateDraft,
} from '../../utils/taskRegenerateDraft';
import { submitVideoGenerationTask } from '../../utils/submitVideoGeneration';
import { VIDEO_GENERATION_RATE_LIMIT_MESSAGE } from '../../utils/videoGenerationRateLimit';
import {
  getLocalStorageValue,
  SEEDANCE_STORAGE_KEYS,
} from '../../utils/seedanceStorage';
import { TaskDetailView } from './TaskDetailView';
import { TaskRegenerateModal } from './TaskRegenerateModal';
import { useTaskDetail } from './useTaskDetail';
import './taskDetail.css';

export type TaskDetailPageProps = {
  taskId: string;
  hasTaskListConfig: () => boolean;
  onNeedConfig: () => void;
};

export const TaskDetailPage: React.FC<TaskDetailPageProps> = ({
  taskId,
  hasTaskListConfig,
  onNeedConfig,
}) => {
  const navigate = useNavigate();
  const apiKey = useSeedanceAuthStore((s) => s.apiKey);
  const connected = useSeedanceAuthStore((s) => s.connected);
  const setTasks = useSeedanceTaskStore((s) => s.setTasks);
  const ensurePolling = useSeedanceTaskStore((s) => s.ensurePolling);
  const addDebug = useSeedanceUiStore((s) => s.addDebug);
  const showToast = useSeedanceUiStore((s) => s.showToast);
  const onTaskDetailDebug = useCallback(
    (msg: string, isError?: boolean) => addDebug(msg, isError),
    [addDebug],
  );

  const { loading, error, detail, materials, materialsLoading } = useTaskDetail(
    taskId,
    onTaskDetailDebug,
  );
  const [regenOpen, setRegenOpen] = useState(false);
  const [regenSubmitting, setRegenSubmitting] = useState(false);

  const regenDraft = useMemo(
    () => (detail ? buildRegenerateDraftFromTaskDetail(detail, materials) : null),
    [detail, materials],
  );

  useEffect(() => {
    if (!hasTaskListConfig()) {
      showToast('请先配置系统访问令牌和用户 ID', 'e');
      onNeedConfig();
      navigate('/tasks', { replace: true });
    }
  }, [hasTaskListConfig, showToast, onNeedConfig, navigate]);

  const goBack = useCallback(() => {
    navigate('/tasks');
  }, [navigate]);

  const openRegen = useCallback(() => {
    if (!connected) {
      showToast('请先连接 API Key', 'e');
      return;
    }
    if (!regenDraft) {
      showToast('任务详情尚未加载完成', 'e');
      return;
    }
    setRegenOpen(true);
  }, [connected, regenDraft, showToast]);

  const handleRegenSubmit = useCallback(
    async (draft: VideoRegenerateDraft) => {
      if (!connected) {
        showToast('请先连接 API Key', 'e');
        return;
      }
      const token = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.apiKey).trim();
      const userId = getLocalStorageValue(SEEDANCE_STORAGE_KEYS.configUserId).trim();
      if (!token || !userId) {
        showToast('请先配置系统访问令牌和用户 ID', 'e');
        onNeedConfig();
        return;
      }

      setRegenSubmitting(true);
      try {
        showToast('正在提交任务…', 'i');
        const { taskId: newId } = await submitVideoGenerationTask(
          {
            apiKey,
            model: draft.model,
            prompts: draft.prompts,
            nodes: draft.nodes,
            duration: draft.duration,
            ratio: draft.ratio,
            resolution: draft.resolution,
            onDebug: (msg, isError) => addDebug(msg, isError),
          },
          { setTasks, ensurePolling },
        );
        showToast(`已新建任务（${newId}），请到任务列表查看进度`, 's');
        setRegenOpen(false);
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : '提交失败';
        showToast(msg, msg === VIDEO_GENERATION_RATE_LIMIT_MESSAGE ? 'i' : 'e');
      } finally {
        setRegenSubmitting(false);
      }
    },
    [
      addDebug,
      apiKey,
      connected,
      ensurePolling,
      onNeedConfig,
      setTasks,
      showToast,
    ],
  );

  return (
    <div className="task-detail-page on">
      <header className="task-detail-toolbar">
        <button type="button" className="task-detail-back-link" onClick={goBack}>
          <span className="task-detail-back-chevron" aria-hidden>
            &lt;
          </span>
          返回
        </button>
        <div className="task-detail-toolbar-actions">
          <button
            type="button"
            className="btn primary"
            onClick={openRegen}
            disabled={loading || !!error || !detail}
          >
            重新生成
          </button>
        </div>
      </header>

      <TaskDetailView
        loading={loading}
        error={error}
        detail={detail}
        queryTaskId={taskId}
        materials={materials}
        materialsLoading={materialsLoading}
      />

      <TaskRegenerateModal
        open={regenOpen}
        initialDraft={regenDraft}
        submitting={regenSubmitting}
        onClose={() => setRegenOpen(false)}
        onSubmit={(d) => void handleRegenSubmit(d)}
        onLimitMessage={(msg) => showToast(msg, 'e')}
      />
    </div>
  );
};

export default TaskDetailPage;
